/**
 * The words on the trouble page: every dead end a viewer can reach, said once.
 *
 * Pure, so the page in the app and the pages the API renders itself (the
 * unsubscribe pages, the no-script form) say the same thing from the same
 * place. Nothing here names a workspace or a host: the host's first name and
 * the workspace's address come in as values.
 */
import type { GIconName } from "./guest-icons";

export type TroubleKind =
  /** The register page for a talk nobody knows, or an old link. */
  | "old"
  /** A watch or Room link with a token nobody knows. */
  | "seat"
  /** A good link opened on an address that is not the workspace's own. */
  | "door"
  /** A talk that exists but has not been published. */
  | "soon"
  /** A talk the host has taken down. */
  | "down"
  /** A failure at our end while loading. */
  | "ours"
  /** No connection at all. */
  | "off"
  /** The register form sent without script, with something to fix. */
  | "form"
  /** The question before an unsubscribe goes through. */
  | "unsub-ask"
  /** An unsubscribe link with a bad or stale signature. */
  | "unsub-bad"
  /** Unsubscribed, done. */
  | "unsub"
  /** A workspace's own address, with no webinar published. */
  | "closed";

export interface TroubleValues {
  /** The host's first name, from the workspace's owner. Empty when unknown. */
  host?: string;
  /** Where a good link really lives, for "door". */
  origin?: string;
  /** The address that was unsubscribed. */
  email?: string;
  /** What the form found wrong, in its own words. */
  message?: string;
}

type TroubleAction = "go" | "again" | "back" | "submit";

interface TroubleCopy {
  title: string;
  body: string;
  /** What the one button does, when there is one. */
  action?: { label: string; kind: TroubleAction };
  /** The quiet line under the buttons. */
  hint?: string;
  /** What that line says once Try again found it no better. */
  tried?: string;
}

/** The address as a person reads it: no scheme, no trailing slash. */
function bareHost(origin: string): string {
  return origin.replace(/^https?:\/\//, "").replace(/\/+$/, "");
}

export function troubleCopy(kind: TroubleKind, v: TroubleValues = {}): TroubleCopy {
  const host = (v.host ?? "").trim();
  const them = host || "the host";
  switch (kind) {
    case "old":
      return {
        title: "This page isn’t here.",
        body: "The link may be old.",
      };
    case "seat":
      return {
        title: "This link doesn’t work any more.",
        body: `Your place in the talk is kept. Use the link from your latest email from ${them}, or ask for a fresh one.`,
      };
    case "door":
      return {
        title: "This talk lives at its own address.",
        body: v.origin ? `Taking you to ${bareHost(v.origin)} now.` : "Taking you there now.",
        action: { label: "Go there now", kind: "go" },
        hint: "If nothing happens in a few seconds, press the button.",
      };
    case "soon":
      return {
        title: "Not open yet.",
        body: `${host || "The host"} hasn’t opened this page yet. Try again a little later.`,
        action: { label: "Try again", kind: "again" },
        tried: "Still not open.",
      };
    case "down":
      return {
        title: "This talk has ended.",
        body: `${host || "The host"} has taken this page down. Anything you were sent is still in your email.`,
      };
    case "ours":
      return {
        title: "Something went wrong at our end.",
        body: "It’s not you. Give it a moment and try again.",
        action: { label: "Try again", kind: "again" },
        tried: "Still stuck? Reload the page.",
      };
    case "off":
      return {
        title: "You’re offline.",
        body: "Check your Wi-Fi or signal. We’ll pick up right where you left off.",
        action: { label: "Try again", kind: "again" },
        tried: "Still no connection. We’ll keep trying.",
      };
    case "form":
      return {
        title: "One more thing.",
        body: v.message?.trim() || "Some details are missing or invalid.",
        action: { label: "Go back", kind: "back" },
      };
    case "unsub-ask":
      return {
        title: "Unsubscribe?",
        body: v.email ? `Stop emails from ${host || "this workspace"} to ${v.email}.` : `Stop emails from ${host || "this workspace"}.`,
        action: { label: "Unsubscribe", kind: "submit" },
        hint: "You can always come back through a new registration.",
      };
    case "unsub-bad":
      return {
        title: "This link doesn’t work.",
        body: "It may be old. Use the unsubscribe link in your latest email.",
      };
    case "closed":
      return {
        title: "No room is open right now.",
        body: `${host || "The host"} hasn’t opened one here yet. Try again a little later, or use the link you were sent.`,
      };
    case "unsub":
      return {
        title: "You’re unsubscribed.",
        body: v.email ? `${v.email} won’t get these emails any more.` : "You won’t get these emails any more.",
        hint: host ? `Changed your mind? Reply to any earlier email and ${host} will put you back.` : undefined,
      };
  }
}

/** The mark drawn above the words, kind by kind, from the rooms' icons (`shared/guest-icons.ts`). */
export function troubleIcon(kind: TroubleKind): GIconName {
  switch (kind) {
    case "old":
    case "seat":
      return "link";
    case "door":
      return "arrowr";
    case "soon":
      return "clock";
    case "down":
    case "closed":
      return "door";
    case "ours":
      return "spark";
    case "off":
      return "wifi";
    case "form":
      return "list";
    case "unsub-ask":
    case "unsub-bad":
      return "mail";
    case "unsub":
      return "check";
  }
}
